import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Send, Clock, Lock, AlertCircle, CheckCircle2, User, Mail, MessageSquare, FileText, RefreshCw, Tag, Plus, Minus } from 'lucide-react';
import { fileRequestApi, authApi } from '../services/api';

export function CreateRequestPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [formData, setFormData] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: 'Secure File Upload Request',
    body: 'You have been invited to upload files securely. Please use the link provided to upload your files safely and securely.',
    expires_in_hours: 72,
    created_by: '',
    cc_emails: [],
    is_reusable: false
  });

  const [ccEmailInput, setCcEmailInput] = useState('');
  const [metadataFields, setMetadataFields] = useState([]);
  const [showMetadata, setShowMetadata] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        // Check if user is authenticated
        if (!authApi.isAuthenticated()) {
          navigate('/login');
          return;
        }

        // Get current user
        const userData = await authApi.getCurrentUser();
        setCurrentUser(userData);
        setFormData(prev => ({
          ...prev,
          created_by: userData.email
        }));
      } catch (err) {
        if (err.response?.status === 401) {
          authApi.logout();
          navigate('/login');
        }
      }
    };

    fetchUser();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCcEmail = () => {
    const email = ccEmailInput.trim();
    console.log('handleAddCcEmail called, email:', email);
    console.log('Current cc_emails:', formData.cc_emails);

    if (email && !formData.cc_emails.includes(email)) {
      console.log('Adding email to cc_emails');
      setFormData(prev => ({
        ...prev,
        cc_emails: [...prev.cc_emails, email]
      }));
      setCcEmailInput('');
    } else {
      console.log('Email not added - already exists or empty');
    }
  };

  const handleRemoveCcEmail = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      cc_emails: prev.cc_emails.filter(email => email !== emailToRemove)
    }));
  };

  const handleCcEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCcEmail();
    }
  };

  const addMetadataField = () => {
    setMetadataFields(prev => [...prev, { key: '', value: '' }]);
  };

  const removeMetadataField = (index) => {
    setMetadataFields(prev => prev.filter((_, i) => i !== index));
  };

  const updateMetadataField = (index, field, val) => {
    setMetadataFields(prev => prev.map((item, i) => i === index ? { ...item, [field]: val } : item));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    console.log('Submitting form with data:', formData);
    console.log('CC emails count:', formData.cc_emails.length);

    // Build metadata object from key-value fields
    const metadataObj = {};
    metadataFields.forEach(({ key, value }) => {
      if (key.trim()) metadataObj[key.trim()] = value;
    });
    const submitData = {
      ...formData,
      ...(Object.keys(metadataObj).length > 0 ? { request_metadata: JSON.stringify(metadataObj) } : {}),
    };

    try {
      const result = await fileRequestApi.create(submitData);
      
      // Redirect to dashboard with success message
      navigate('/dashboard', { 
        state: { 
          notification: {
            type: 'success',
            title: 'File Request Sent Successfully',
            message: `Your secure file upload request has been sent to ${result.recipient_name}. They will receive an email with the upload link.`,
            recipient: result.recipient_name,
            uploadUrl: result.upload_url
          }
        }
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create file request');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-lg mb-4">
            <Shield className="w-8 h-8 text-primary-500" aria-hidden="true" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create Secure File Request
          </h1>
          <p className="text-lg text-gray-600">
            Request files from others with enterprise-grade security and encryption
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="p-6">
                {success && (
                  <div className="mb-6 p-4 bg-security-50 border border-security-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-security-600 mt-0.5" aria-hidden="true" />
                      <div>
                        <h3 className="text-sm font-medium text-security-800">
                          Request Created Successfully!
                        </h3>
                        <p className="text-sm text-security-700 mt-1">
                          A secure email has been sent to <strong>{success.recipient}</strong> with upload instructions.
                        </p>
                        <div className="mt-3">
                          <button
                            onClick={() => navigate('/dashboard')}
                            className="text-sm font-medium text-security-700 hover:text-security-800"
                          >
                            View in Dashboard →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-5 h-5 text-red-600" aria-hidden="true" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Recipient Information */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <User className="w-5 h-5" aria-hidden="true" />
                      <span>Recipient Information</span>
                    </h2>
                    
                    <div>
                      <label htmlFor="recipient_name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="recipient_name"
                        name="recipient_name"
                        required
                        value={formData.recipient_name}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="John Doe"
                        aria-describedby="recipient_name_help"
                      />
                      <p id="recipient_name_help" className="text-xs text-gray-500 mt-1">
                        The name of the person you're requesting files from
                      </p>
                    </div>

                    <div>
                      <label htmlFor="recipient_email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="recipient_email"
                        name="recipient_email"
                        required
                        value={formData.recipient_email}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="john@example.com"
                        aria-describedby="recipient_email_help"
                      />
                      <p id="recipient_email_help" className="text-xs text-gray-500 mt-1">
                        A secure upload link will be sent to this email address
                      </p>
                    </div>

                    <div>
                      <label htmlFor="cc_emails" className="block text-sm font-medium text-gray-700 mb-2">
                        CC Notifications (Optional)
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="email"
                          id="cc_emails"
                          value={ccEmailInput}
                          onChange={(e) => setCcEmailInput(e.target.value)}
                          onKeyPress={handleCcEmailKeyPress}
                          className="input-field flex-1"
                          placeholder="email@example.com"
                          aria-describedby="cc_emails_help"
                        />
                        <button
                          type="button"
                          onClick={handleAddCcEmail}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                        >
                          Add
                        </button>
                      </div>
                      <p id="cc_emails_help" className="text-xs text-gray-500 mt-1">
                        Additional recipients who will be notified when the request is sent and completed
                      </p>
                      {formData.cc_emails.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {formData.cc_emails.map((email, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                            >
                              {email}
                              <button
                                type="button"
                                onClick={() => handleRemoveCcEmail(email)}
                                className="ml-2 text-gray-500 hover:text-gray-700"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email Content */}
                  <div className="space-y-4 border-t pt-6">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <MessageSquare className="w-5 h-5" aria-hidden="true" />
                      <span>Email Content</span>
                    </h2>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject Line
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="Secure File Upload Request"
                      />
                    </div>

                    <div>
                      <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-2">
                        Message Body
                      </label>
                      <textarea
                        id="body"
                        name="body"
                        rows={4}
                        value={formData.body}
                        onChange={handleInputChange}
                        className="input-field resize-none"
                        placeholder="Please upload the requested files using the secure link provided..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This message will be included in the email along with security information
                      </p>
                    </div>
                  </div>

                  {/* Expiration Settings */}
                  <div className="space-y-4 border-t pt-6">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <Clock className="w-5 h-5" aria-hidden="true" />
                      <span>Security Settings</span>
                    </h2>

                    <div>
                      <label htmlFor="expires_in_hours" className="block text-sm font-medium text-gray-700 mb-2">
                        Link Expiration
                      </label>
                      <select
                        id="expires_in_hours"
                        name="expires_in_hours"
                        value={formData.expires_in_hours}
                        onChange={handleInputChange}
                        className="input-field"
                      >
                        <option value={24}>24 hours</option>
                        <option value={48}>2 days</option>
                        <option value={72}>3 days (recommended)</option>
                        <option value={168}>1 week</option>
                        <option value={336}>2 weeks</option>
                        <option value={720}>30 days</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        The upload link will automatically expire after this time
                      </p>
                    </div>

                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center h-5">
                        <input
                          id="is_reusable"
                          name="is_reusable"
                          type="checkbox"
                          checked={formData.is_reusable}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            is_reusable: e.target.checked
                          }))}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor="is_reusable" className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                          <RefreshCw className="w-4 h-4" aria-hidden="true" />
                          <span>Reusable Link</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          Allow multiple uploads using the same link until it expires.
                          Useful when you need to collect files over time or from multiple sessions.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Metadata (collapsed by default) */}
                  <div className="border-t pt-6">
                    <button
                      type="button"
                      onClick={() => setShowMetadata(!showMetadata)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <Tag className="w-5 h-5" aria-hidden="true" />
                        <span>Metadata (Optional)</span>
                      </h2>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${showMetadata ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showMetadata && (
                      <div className="mt-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            Attach key-value pairs for internal tracking (e.g. case ID, project name). Not visible to the recipient.
                          </p>
                          <button
                            type="button"
                            onClick={addMetadataField}
                            className="text-sm text-primary-600 hover:text-primary-800 flex items-center space-x-1 flex-shrink-0 ml-4"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Add field</span>
                          </button>
                        </div>
                        {metadataFields.length > 0 && (
                          <div className="space-y-2">
                            {metadataFields.map((item, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={item.key}
                                  onChange={(e) => updateMetadataField(index, 'key', e.target.value)}
                                  placeholder="Key"
                                  className="flex-1 input-field"
                                />
                                <input
                                  type="text"
                                  value={item.value}
                                  onChange={(e) => updateMetadataField(index, 'value', e.target.value)}
                                  placeholder="Value"
                                  className="flex-1 input-field"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeMetadataField(index)}
                                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                  title="Remove field"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="pt-6">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn-primary w-full flex items-center justify-center space-x-3"
                    >
                      {isLoading ? (
                        <>
                          <div className="spinner w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                          <span>Creating Request...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" aria-hidden="true" />
                          <span>Send Secure Request</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Security Info Sidebar */}
          <div className="space-y-6">
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Lock className="w-5 h-5 text-security-600" aria-hidden="true" />
                  <span>Security Features</span>
                </h3>
                
                <div className="space-y-4 text-sm text-gray-600">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-4 h-4 text-security-500 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="font-medium text-gray-900">End-to-End Encryption</p>
                      <p>Files are encrypted using AES-256</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-4 h-4 text-security-500 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="font-medium text-gray-900">Secure Links</p>
                      <p>Cryptographically secure, non-guessable URLs</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-4 h-4 text-security-500 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="font-medium text-gray-900">Auto-Expiration</p>
                      <p>Links automatically expire for added security</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-4 h-4 text-security-500 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="font-medium text-gray-900">Zero-Knowledge</p>
                      <p>We cannot access your files or see their contents</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-primary-600" aria-hidden="true" />
                  <span>What Happens Next?</span>
                </h3>
                
                <ol className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start space-x-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-xs font-semibold">1</span>
                    <span>Secure email sent to recipient with upload link</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-xs font-semibold">2</span>
                    <span>Recipient uploads files (automatically encrypted)</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-xs font-semibold">3</span>
                    <span>You receive notification and can access files</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-xs font-semibold">4</span>
                    <span>Files are securely stored until you download them</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
